"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import { BillingToggle } from "@/features/billing/components/billing-toggle";
import { PlanCard } from "@/features/billing/components/plan-card";
import { SubscribeModal } from "@/features/billing/components/subscribe-modal";
import { CreditBreakdownModal } from "@/features/billing/components/credit-breakdown-modal";
import {
  usePlans,
  useSubscription,
  type PlanResponse,
} from "@/features/billing/hooks/use-subscription";
import { ChoosePlanPending } from "./pending";

interface StepChoosePlanProps {
  onComplete: () => void;
  submitting: boolean;
}

export function StepChoosePlan({
  onComplete,
  submitting,
}: StepChoosePlanProps) {
  const queryClient = useQueryClient();
  const { data: plans, isLoading: plansLoading } = usePlans();
  // Registering an active observer for the subscription query is critical:
  // it allows invalidateQueries to trigger a real refetch so we can detect
  // a successful purchase, and also catches the "already subscribed on
  // refresh" case.
  const { data: subscription } = useSubscription();

  const [cadence, setCadence] = useState<"annual" | "monthly">("monthly");
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [subscribeModal, setSubscribeModal] = useState<{
    plan: PlanResponse;
    cadence: "monthly" | "annual";
    seatCount: number;
  } | null>(null);

  // Auto-advance when a paid subscription is detected — covers both the
  // post-purchase path (subscription updates after invalidation) and the
  // refresh path (subscription already active on mount).
  const completedRef = useRef(false);
  useEffect(() => {
    if (completedRef.current || submitting) return;
    if (subscription?.plan?.slug && subscription.plan.slug !== "free") {
      completedRef.current = true;
      onComplete();
    }
  }, [subscription, onComplete, submitting]);

  const displayPlans = plans?.filter((p) => p.slug !== "free") ?? [];

  const annualSavings = (() => {
    const firstPaid = displayPlans[0];
    if (!firstPaid) return 0;
    const monthly = (firstPaid.price_monthly_cents ?? 0) / 100;
    const annualMonthly =
      Math.round((firstPaid.price_annual_cents ?? 0) / 12) / 100;
    return Math.round((monthly - annualMonthly) * 12);
  })();

  const handleUpgrade = useCallback(
    (planSlug: string, seatCount: number) => {
      const selectedPlan = plans?.find((p) => p.slug === planSlug);
      if (!selectedPlan) return;
      setSubscribeModal({ plan: selectedPlan, cadence, seatCount });
    },
    [plans, cadence],
  );

  // Invalidating the subscription query triggers a refetch via the active
  // useSubscription() observer above. When the fresh data shows a paid
  // plan, the useEffect fires and calls onComplete().
  const handleSubscribeModalClose = useCallback(async () => {
    setSubscribeModal(null);
    await queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
  }, [queryClient]);

  if (plansLoading) {
    return <ChoosePlanPending />;
  }

  return (
    <>
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        {/* Billing toggle + Enterprise link */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between shrink-0">
          <BillingToggle
            cadence={cadence}
            onCadenceChange={setCadence}
            annualSavings={annualSavings}
          />
          <a
            href="mailto:sales@arbi.ai"
            className="flex items-center gap-1.5 text-[13px] text-contrast/50 hover:text-contrast/70 transition-colors shrink-0"
          >
            <PlanIcon slug="enterprise" width={16} height={16} />
            Need Enterprise?
            <span className="text-[#9370DB]">Contact us</span>
            <Icon
              name="IconArrowUpRight"
              size={12}
              className="text-[#9370DB]"
            />
          </a>
        </div>

        {/* Plan cards — 3 in a single row, stretch to fill */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:flex-1 lg:min-h-0">
          {displayPlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              cadence={cadence}
              isCurrentPlan={false}
              isDowngrade={false}
              isRecommended={plan.slug === "growth"}
              isUpgrading={false}
              hideSeats
              onUpgrade={handleUpgrade}
              onBreakdownClick={() => setShowBreakdown(true)}
            />
          ))}
        </div>
      </div>

      <CreditBreakdownModal
        open={showBreakdown}
        onOpenChange={setShowBreakdown}
      />

      {subscribeModal && (
        <SubscribeModal
          open
          onClose={handleSubscribeModalClose}
          plan={subscribeModal.plan}
          initialCadence={subscribeModal.cadence}
          initialSeatCount={subscribeModal.seatCount}
        />
      )}
    </>
  );
}
